import { useState, useEffect } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Input,
  Grid,
  Badge,
  AccordionRoot,
  AccordionItem,
  AccordionItemTrigger,
  AccordionItemContent,
} from '@chakra-ui/react'
import { Mail, Clock, CheckCircle, ChevronDown, Send } from 'lucide-react'
import { Field } from '@/components/ui/field'
import { NativeSelectField, NativeSelectRoot } from '@/components/ui/native-select'
import { Textarea } from '@chakra-ui/react'

const CONTACT_REASONS = [
  { value: 'demo', label: 'Demander une démo' },
  { value: 'pricing', label: 'Question sur les tarifs' },
  { value: 'support', label: 'Support technique' },
  { value: 'partnership', label: 'Partenariat' },
  { value: 'migration', label: 'Migration de données' },
  { value: 'other', label: 'Autre' },
]

const faqItems = [
  // ========================================
  // 1. ESSAI ET ENGAGEMENT
  // ========================================
  {
    category: 'Essai et engagement',
    question: 'Comment fonctionne l\'essai gratuit de 7 jours ?',
    answer:
      'Créez votre compte en 2 minutes, aucune carte bancaire requise. Vous avez 7 jours complets pour tester toutes les fonctionnalités. Si vous n\'êtes pas satisfait, vous ne payez rien.',
  },
  {
    category: 'Essai et engagement',
    question: 'Puis-je annuler à tout moment ?',
    answer:
      'Oui, absolument. Kopro fonctionne sans engagement. Vous pouvez annuler votre abonnement à tout moment depuis votre espace client, en un clic. Aucuns frais de résiliation, aucune justification demandée.',
  },
  {
    category: 'Essai et engagement',
    question: 'Que se passe-t-il si je change d\'offre ?',
    answer:
      'Vous pouvez changer d\'offre à tout moment, en montée ou descente de gamme. Votre abonnement est ajusté au prorata, aucun engagement de durée.',
  },

  // ========================================
  // 2. MIGRATION ET MISE EN PLACE
  // ========================================
  {
    category: 'Migration et mise en place',
    question: 'Combien de temps prend la mise en place ?',
    answer:
      'La mise en place est rapide : vous pouvez commencer à utiliser Kopro dès l\'inscription. Pour une installation complète avec import de données, comptez 2-3 jours avec l\'accompagnement de notre équipe.',
  },
  {
    category: 'Migration et mise en place',
    question: 'Puis-je importer mes données existantes ?',
    answer:
      'Oui, absolument. Notre équipe vous accompagne gratuitement dans la migration de vos données (résidences, lots, copropriétaires, documents). Comptez 2-3 jours pour une installation complète.',
  },
  {
    category: 'Migration et mise en place',
    question: 'Comment se passe la migration depuis mon outil actuel ?',
    answer:
      'Notre équipe technique récupère vos données depuis votre outil actuel (fichiers Excel, CSV, export logiciel), nettoie et structure les données, puis les importe dans Kopro. L\'accompagnement est gratuit et nous assurons une migration sans perte de données.',
  },
  {
    category: 'Migration et mise en place',
    question: 'Qu\'est-ce qui est inclus dans l\'accompagnement à la migration ?',
    answer:
      'L\'accompagnement inclut : analyse de vos besoins, extraction et nettoyage de vos données, import complet dans Kopro, configuration personnalisée de votre espace, formation de votre équipe, et support dédié pendant les 30 premiers jours.',
  },

  // ========================================
  // 3. FONCTIONNALITÉS
  // ========================================
  {
    category: 'Fonctionnalités',
    question: 'Mes copropriétaires devront-ils créer un compte ?',
    answer:
      'Non, vos copropriétaires n\'ont pas besoin de créer de compte. Ils accèdent à leur espace copropriétaire via un lien unique envoyé par email. Pas de mot de passe à retenir, connexion ultra-simple.',
  },
  {
    category: 'Fonctionnalités',
    question: 'Kopro remplace-t-il mon logiciel comptable ?',
    answer:
      'Kopro intègre une comptabilité complète adaptée aux copropriétés (appels de fonds, suivi des paiements, budget prévisionnel). Il peut remplacer votre logiciel comptable pour la gestion courante. Pour la comptabilité approfondie, Kopro s\'intègre avec vos outils existants.',
  },
  {
    category: 'Fonctionnalités',
    question: 'Les appels de fonds sont-ils automatisés ?',
    answer:
      'Oui, les appels de fonds sont entièrement automatisés : génération automatique selon votre calendrier, envoi par email aux copropriétaires, suivi des paiements en temps réel, et relances automatiques pour les impayés.',
  },
  {
    category: 'Fonctionnalités',
    question: 'Puis-je garder mon compte bancaire actuel ?',
    answer:
      'Oui, Kopro n\'impose aucun changement de compte bancaire. Vous conservez vos comptes bancaires actuels et Kopro se connecte simplement pour le suivi des flux (import des relevés bancaires).',
  },
  {
    category: 'Fonctionnalités',
    question: 'Faut-il former mes copropriétaires ?',
    answer:
      'Non, aucune formation nécessaire. Le portail copropriétaire est ultra-simple : ils reçoivent un email, cliquent sur un lien, c\'est tout. Pas de compte à créer, pas de mot de passe à retenir.',
  },

  // ========================================
  // 4. SÉCURITÉ ET CONFORMITÉ
  // ========================================
  {
    category: 'Sécurité et conformité',
    question: 'Mes données sont-elles sécurisées ?',
    answer:
      'Vos données sont hébergées en France (certifié HDS), chiffrées en transit et au repos. Sauvegardes quotidiennes automatiques. Conformité RGPD garantie. Vous gardez le contrôle total de vos données.',
  },
  {
    category: 'Sécurité et conformité',
    question: 'Kopro est-il conforme à la loi ALUR et à la loi ÉLAN ?',
    answer:
      'Oui, Kopro est 100% conforme aux lois ALUR et ÉLAN. La plateforme respecte toutes les obligations légales : tenue du registre d\'immatriculation, fonds de travaux, votes en AG, information des copropriétaires, etc.',
  },

  // ========================================
  // 5. SUPPORT ET FORMATION
  // ========================================
  {
    category: 'Support et formation',
    question: 'Le support est-il vraiment inclus ?',
    answer:
      'Oui, le support est inclus dans tous les plans. Essentiel : email (réponse sous 24h). Professionnel : email prioritaire + chat (réponse sous 4h). Intégral : support dédié 24/7 par téléphone.',
  },
  {
    category: 'Support et formation',
    question: 'Proposez-vous une formation pour mon équipe ?',
    answer:
      'Oui, une formation complète est incluse : session de démarrage personnalisée, documentation détaillée, vidéos tutoriels, et webinaires réguliers. Pour l\'offre Intégral, formation sur site possible.',
  },
]

export function ContactPage() {
  const [searchParams] = useSearchParams()
  const [formData, setFormData] = useState({
    email: '',
    reason: 'demo',
    customReason: '',
    message: '',
  })
  const [isSubmitted, setIsSubmitted] = useState(false)

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Initialize form with URL parameter if present
  useEffect(() => {
    const subject = searchParams.get('subject')
    if (subject === 'demo') {
      setFormData(prev => ({ ...prev, reason: 'demo' }))
    }
  }, [searchParams])

  const bg = {
    primary: 'white',
    secondary: 'gray.50',
    tertiary: 'gray.100',
    card: 'white',
  }

  const border = {
    default: 'gray.200',
    subtle: 'gray.300',
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // TODO: Implement form submission logic
    console.log('Form submitted:', formData)
    setIsSubmitted(true)
  }

  return (
    <Box bg={"white"} color={"gray.900"} minH="100vh">
      {/* Hero Section */}
      <Box
        py={{ base: 20, md: 32 }}
        bg={"gray.50"}
        position="relative"
        overflow="hidden"
      >
        {/* Gradient Background */}
        <Box
          position="absolute"
          inset={0}
          bg="radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.1), transparent 70%)"
        />
        {/* Grid Pattern */}
        <Box
          position="absolute"
          inset={0}
          bgImage="linear-gradient(to right, rgba(16, 185, 129, 0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(16, 185, 129, 0.05) 1px, transparent 1px)"
          bgSize="60px 60px"
          opacity={0.3}
        />

        <Container maxW="container.lg" position="relative" zIndex={1}>
          <VStack gap={6} textAlign="center" maxW="800px" mx="auto">
            <Heading as="h1" fontSize={{ base: '4xl', md: '5xl', lg: '6xl' }} fontWeight="bold">
              Contactez-nous
            </Heading>
            <Text fontSize={{ base: 'lg', md: 'xl' }} color={"gray.600"}>
              Une question ? Besoin d'aide ? Notre équipe est là pour vous accompagner.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Contact Form & Info Section */}
      <Container maxW="container.lg" py={{ base: 16, md: 24 }}>
        <Grid templateColumns={{ base: '1fr', lg: '1fr 400px' }} gap={12}>
          {/* Left: Contact Form */}
          <Box>
            {isSubmitted ? (
              <Box
                p={8}
                bg={"white"}
                borderRadius="2xl"
                border="1px solid"
                borderColor={"gray.200"}
                textAlign="center"
              >
                <VStack gap={6}>
                  <Box
                    w="80px"
                    h="80px"
                    borderRadius="full"
                    bg="brand.100"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <CheckCircle size={40} color="#10b981" />
                  </Box>
                  <Heading as="h3" size="lg">
                    Message envoyé !
                  </Heading>
                  <Text color={"gray.600"}>
                    Merci pour votre message. Notre équipe vous répondra dans les 48h (jours ouvrés).
                  </Text>
                  <Button
                    onClick={() => {
                      setIsSubmitted(false)
                      setFormData({ email: '', reason: 'demo', customReason: '', message: '' })
                    }}
                    variant="outline"
                    borderColor={border.subtle}
                  >
                    Envoyer un autre message
                  </Button>
                </VStack>
              </Box>
            ) : (
              <Box
                as="form"
                onSubmit={handleSubmit}
                p={8}
                bg={"white"}
                borderRadius="2xl"
                border="1px solid"
                borderColor={"gray.200"}
              >
                <VStack gap={6} align="stretch">
                  <Field label="Votre email" required>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="vous@exemple.com"
                      size="lg"
                      required
                    />
                  </Field>

                  <Field label="Motif de contact" required>
                    <NativeSelectRoot size="lg">
                      <NativeSelectField
                        value={formData.reason}
                        onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                      >
                        {CONTACT_REASONS.map((reason) => (
                          <option key={reason.value} value={reason.value}>
                            {reason.label}
                          </option>
                        ))}
                      </NativeSelectField>
                    </NativeSelectRoot>
                  </Field>

                  {formData.reason === 'other' && (
                    <Field label="Précisez le motif" required>
                      <Input
                        value={formData.customReason}
                        onChange={(e) => setFormData({ ...formData, customReason: e.target.value })}
                        placeholder="Décrivez brièvement..."
                        size="lg"
                        required
                      />
                    </Field>
                  )}

                  <Field label="Votre message" required>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Décrivez votre demande en détail..."
                      rows={6}
                      required
                    />
                  </Field>

                  <Button
                    type="submit"
                    size="lg"
                    colorPalette="brand"
                    bg="brand.600"
                    color="white"
                    _hover={{ bg: 'brand.700' }}
                  >
                    <Send size={20} style={{ marginRight: '8px' }} />
                    Envoyer le message
                  </Button>
                </VStack>
              </Box>
            )}
          </Box>

          {/* Right: Contact Info */}
          <VStack gap={6} align="stretch">
            {/* Support Info Card */}
            <Box
              p={6}
              bg={"white"}
              borderRadius="xl"
              border="1px solid"
              borderColor={"gray.200"}
            >
              <VStack align="start" gap={4}>
                <HStack gap={3}>
                  <Box
                    p={2}
                    bg="brand.100"
                    borderRadius="lg"
                  >
                    <Mail size={24} color="#10b981" />
                  </Box>
                  <Heading as="h3" size="md">
                    Support par email
                  </Heading>
                </HStack>
                <Text color={"gray.600"} fontSize="sm">
                  Envoyez-nous un message via le formulaire. Notre équipe vous répondra dans les meilleurs délais.
                </Text>
              </VStack>
            </Box>

            {/* Response Time Card */}
            <Box
              p={6}
              bg={"white"}
              borderRadius="xl"
              border="1px solid"
              borderColor={"gray.200"}
            >
              <VStack align="start" gap={4}>
                <HStack gap={3}>
                  <Box
                    p={2}
                    bg="brand.100"
                    borderRadius="lg"
                  >
                    <Clock size={24} color="#10b981" />
                  </Box>
                  <Heading as="h3" size="md">
                    Temps de réponse
                  </Heading>
                </HStack>
                <Text color={"gray.600"} fontSize="sm">
                  Réponse sous 48h les jours ouvrés. Pour les urgences techniques, nos clients bénéficient d'un support prioritaire.
                </Text>
              </VStack>
            </Box>

            {/* Quick Links */}
            <Box
              p={6}
              bg={bg.tertiary}
              borderRadius="xl"
            >
              <VStack align="start" gap={3}>
                <Text fontWeight="semibold">Besoin d'une réponse rapide ?</Text>
                <RouterLink to="/#pricing">
                  <Button
                    size="sm"
                    variant="ghost"
                    color="brand.600"
                    _hover={{ bg: "white" }}
                    w="full"
                    justifyContent="flex-start"
                  >
                    Consulter nos tarifs
                  </Button>
                </RouterLink>
                <RouterLink to="/">
                  <Button
                    size="sm"
                    variant="ghost"
                    color="brand.600"
                    _hover={{ bg: "white" }}
                    w="full"
                    justifyContent="flex-start"
                  >
                    Retour à l'accueil
                  </Button>
                </RouterLink>
              </VStack>
            </Box>
          </VStack>
        </Grid>
      </Container>

      {/* FAQ Section */}
      <Box py={{ base: 16, md: 24 }} bg={"gray.50"}>
        <Container maxW="800px">
          <VStack gap={12}>
            <VStack gap={6} textAlign="center">
              <Badge
                size="md"
                px={3}
                py={1.5}
                borderRadius="full"
                bg="brand.100"
                color="brand.600"
                border="1px solid"
                borderColor="brand.200"
              >
                FAQ
              </Badge>
              <Heading as="h2" fontSize={{ base: '4xl', md: '5xl' }} fontWeight="bold">
                Questions fréquentes
              </Heading>
              <Text fontSize="lg" color={"gray.600"}>
                Consultez nos réponses aux questions les plus courantes
              </Text>
            </VStack>

            <VStack gap={10} align="stretch" w="full">
              {/* Group FAQ items by category */}
              {Object.entries(
                faqItems.reduce((acc, item) => {
                  const category = item.category || 'Autres'
                  if (!acc[category]) {
                    acc[category] = []
                  }
                  acc[category].push(item)
                  return acc
                }, {} as Record<string, typeof faqItems>)
              ).map(([category, items], categoryIndex) => (
                <VStack key={categoryIndex} gap={4} align="stretch">
                  {/* Category Title */}
                  <Heading
                    as="h3"
                    fontSize={{ base: 'xl', md: '2xl' }}
                    fontWeight="semibold"
                    color="brand.600"
                    borderBottom="2px solid"
                    borderColor="brand.200"
                    pb={2}
                  >
                    {category}
                  </Heading>

                  {/* Questions in this category */}
                  <AccordionRoot collapsible>
                    {items.map((item, index) => (
                      <AccordionItem
                        key={index}
                        value={`item-${categoryIndex}-${index}`}
                      >
                        <AccordionItemTrigger
                          py={6}
                          px={6}
                          bg={"white"}
                          borderRadius="xl"
                          border="1px solid"
                          borderColor={"gray.200"}
                          mb={4}
                          _hover={{ bg: bg.tertiary }}
                          cursor="pointer"
                        >
                          <HStack justify="space-between" w="full">
                            <Text fontWeight="semibold" fontSize="lg" textAlign="left">
                              {item.question}
                            </Text>
                            <ChevronDown size={20} />
                          </HStack>
                        </AccordionItemTrigger>
                        <AccordionItemContent px={6} pb={6}>
                          <Text color={"gray.600"} lineHeight="tall">
                            {item.answer}
                          </Text>
                        </AccordionItemContent>
                      </AccordionItem>
                    ))}
                  </AccordionRoot>
                </VStack>
              ))}
            </VStack>
          </VStack>
        </Container>
      </Box>
    </Box>
  )
}
